import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VerseChat from "./VerseChat";
import { formatMessage } from "../locales";
import { useLanguage } from "../contexts/LanguageContext";

// You may want to pass these as props or import from a shared file
const HIGHLIGHT_COLORS = [
  "#fff59d", // yellow
  "#90caf9", // blue
  "#a5d6a7", // green
  "#ffab91", // orange
  "#f48fb1", // pink
  "#ce93d8", // purple
];
const DARK_HIGHLIGHT_COLORS = [
  "#e6b800", // amber
  "#4a6fa5", // muted blue
  "#4a8c4a", // muted green
  "#e67700", // amber orange
  "#a35c7a", // muted pink
  "#8c5c99", // muted purple
];

const VerseDrawer = ({
  open,
  onClose,
  verse,
  bookName,
  chapterNumber,
  highlightColor,
  setHighlightColor,
  getHighlight,
  setHighlight,
  fontType = "default",
  theme,
  historicalDocs = [],
  onHighlightChange,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const { messages } = useLanguage();
  const highlightColors =
    theme?.palette?.mode === "dark" ? DARK_HIGHLIGHT_COLORS : HIGHLIGHT_COLORS;
  const [selectedColorIndex, setSelectedColorIndex] = useState(null);

  useEffect(() => {
    const book = bookName || verse?.bookName;
    if (verse && getHighlight && setHighlightColor && book) {
      const colorIndex = getHighlight(book, chapterNumber, verse.number);
      setHighlightColor(colorIndex);
      setSelectedColorIndex(colorIndex);
    }
  }, [verse, bookName, chapterNumber, getHighlight, setHighlightColor]);

  // Clear chat when verse changes
  useEffect(() => {
    setChatMessages([]);
    setChatInput("");
    setIsChatLoading(false);
  }, [verse]);

  const handleShare = async () => {
    if (!verse) return;
    const reference = `${bookName} ${chapterNumber}:${verse.number}`;
    const shareUrl = `${window.location.origin}/read/book/${bookName}/chapter/${chapterNumber}/verse/${verse.number}`;
    const shareText = `${reference}\n"${verse.text}"\n\nRead more at OpenCreed:`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: reference,
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        if (error.name !== "AbortError") {
          await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
          setSnackbarMessage("Link copied to clipboard!");
          setSnackbarOpen(true);
        }
      }
    } else {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      setSnackbarMessage("Link copied to clipboard!");
      setSnackbarOpen(true);
    }
  };

  const handleHighlight = (colorIndex) => {
    const book = bookName || verse?.bookName;
    setHighlightColor?.(colorIndex);
    setSelectedColorIndex(colorIndex);
    setHighlight?.(book, chapterNumber, verse?.number, colorIndex);
    onHighlightChange?.();
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  if (!verse) return null;

  // Font family mapping
  const fontFamilyMap = {
    default: undefined,
    opendyslexic: "OpenDyslexic, Arial, sans-serif",
    serif: "Georgia, Times New Roman, serif",
    sans: "Arial, Helvetica, sans-serif",
    mono: "Courier New, monospace",
    merriweather: "Merriweather, Georgia, serif",
    lato: "Lato, Arial, sans-serif",
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      PaperProps={{
        sx: {
          height: { xs: "80vh", sm: 500 },
          maxHeight: "95vh",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          bgcolor: "background.paper",
          width: { xs: "100vw", md: 960 },
          mx: { md: "auto" },
        },
      }}
      sx={{
        zIndex: 1300,
        "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.5)" },
      }}
      disableSwipeToOpen={false}
      disableDiscovery={false}
    >
      <Box
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          fontFamily: fontFamilyMap[fontType],
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" color="primary">
            {bookName} {chapterNumber}:{verse?.number}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title={navigator.share ? "Share verse" : "Copy link"}>
              <IconButton onClick={handleShare} size="small">
                {navigator.share ? <ShareIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          variant="fullWidth"
        >
          <Tab label="Verse" />
          <Tab label="AI Chat" />
          <Tab label="Historical Sources" />
        </Tabs>
        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {activeTab === 0 && (
            <Box>
              <Typography
                variant="body1"
                sx={{ whiteSpace: "pre-wrap", mb: 3 }}
              >
                {verse?.text}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <span role="img" aria-label="palette">
                    🎨
                  </span>{" "}
                  Highlight
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {highlightColors.map((color, idx) => (
                    <Box
                      key={color}
                      onClick={() => handleHighlight(idx)}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: color,
                        border:
                          selectedColorIndex === idx
                            ? "2px solid #333"
                            : "2px solid #fff",
                        cursor: "pointer",
                        boxShadow: selectedColorIndex === idx ? 2 : 0,
                        transition: "border 0.2s, box-shadow 0.2s",
                      }}
                    />
                  ))}
                  {/* Clear highlight */}
                  <Box
                    onClick={() => handleHighlight(null)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: "#eee",
                      border:
                        selectedColorIndex === null
                          ? "2px solid #333"
                          : "2px solid #fff",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 18,
                      color: "#888",
                    }}
                  >
                    ×
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
          {activeTab === 1 && (
            <Box sx={{ height: "100%" }}>
              <VerseChat
                verse={verse}
                bookName={bookName}
                chapterNumber={chapterNumber}
                messages={chatMessages}
                setMessages={setChatMessages}
                input={chatInput}
                setInput={setChatInput}
                isLoading={isChatLoading}
                setIsLoading={setIsChatLoading}
                fontType={fontType}
              />
            </Box>
          )}
          {activeTab === 2 && (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 1,
                "&::-webkit-scrollbar": { height: "6px" },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "3px",
                  "&:hover": { background: "#555" },
                },
              }}
            >
              {historicalDocs && historicalDocs.length > 0 ? (
                historicalDocs.map((doc) => {
                  if (!doc.isValidBook(bookName)) return null;
                  const link = doc.getLink(
                    bookName,
                    chapterNumber,
                    verse?.number
                  );
                  if (!link) return null;
                  return (
                    <Box
                      key={doc.name}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={
                          <span role="img" aria-label="document">
                            📄
                          </span>
                        }
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {doc.getDisplayName()}
                      </Button>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          textAlign: "center",
                          maxWidth: 200,
                          fontSize: "0.7rem",
                        }}
                      >
                        {doc.description}
                      </Typography>
                    </Box>
                  );
                })
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2 }}
                >
                  No historical sources available for this verse.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </SwipeableDrawer>
  );
};

export default VerseDrawer;
